import { FunctionComponent, ReactNode, RefCallback } from 'react';
import cn from 'classnames';
import { Appearance as AppearanceType } from '@/types';
import { isBrightColor } from '@/utils/colors/isBrightColor';
import { CheckMark } from '@/components/common/icons/CheckMark';
import './Appearance.scss';

type PartialAppearance = Pick<AppearanceType, 'id' | 'colors' | 'name'> &
  Partial<Pick<AppearanceType, 'resources' | 'texture'>>;

interface AppearanceProps {
  appearance: PartialAppearance;
  children?: ReactNode;
}

const AppearanceBubble: FunctionComponent<AppearanceProps> = ({ appearance, children }) => (
  <span className="sprd-appearance__bubble">
    {appearance.texture ? (
      <span
        className="sprd-appearance__bubble__inner"
        style={{ background: `url('${appearance.resources?.[0].href},width=40.png')` }}
      />
    ) : (
      <span className="sprd-appearance__bubble__inner">
        {appearance.colors.map((color) => (
          <span
            className="sprd-appearance__bubble__color"
            style={{
              background: color.value,
              flex: appearance.colors.length - color.index,
            }}
            key={color.index}
          />
        ))}
      </span>
    )}
    {children}
  </span>
);

interface AppearanceBtnProps<GenericAppearance extends PartialAppearance> {
  appearance: GenericAppearance;
  onClick: (appearance: GenericAppearance) => void;
  selected: boolean;
  activeRef?: RefCallback<HTMLButtonElement | null> | undefined;
  small?: boolean;
  disabled?: boolean;
  children?: ReactNode;
}

const getAppearanceClass = (appearance: PartialAppearance, small?: boolean, selected?: boolean) =>
  cn(
    'sprd-appearance',
    selected && 'sprd-appearance--selected',
    appearance.colors.some((c) => isBrightColor(c.value)) && 'sprd-appearance--bright',
    small && 'sprd-appearance--small'
  );

export const Appearance = <GenericAppearance extends PartialAppearance>({
  appearance,
  selected,
  onClick,
  activeRef,
  small,
  disabled,
  children,
}: AppearanceBtnProps<GenericAppearance>) => (
  <button
    ref={activeRef}
    type="button"
    className={getAppearanceClass(appearance, small, selected)}
    onClick={() => onClick(appearance)}
    title={appearance.name}
    role="option"
    disabled={disabled}
    aria-selected={selected}
    data-appearance={appearance.id}
  >
    <AppearanceBubble appearance={appearance}>
      {selected && <CheckMark className="sprd-appearance__bubble__icon" />}
    </AppearanceBubble>
    {children}
  </button>
);

export const StaticAppearance: FunctionComponent<AppearanceProps> = ({ appearance, children }) => (
  <span className={getAppearanceClass(appearance)}>
    <AppearanceBubble appearance={appearance} />
    {children}
  </span>
);
